import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";
import AppContext from "@/context/appContext";
import classNames from "classnames";
import Button from "./button";
import Input from "./input";

type Input = {
  name: string;
  type: string;
  label: string;
  id: string;
  value: string | undefined;
  cols?: string;
};

export default function DetailsInputs({
  register,
  errors,
  watch,
}: {
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}) {
  const { t } = useTranslation("main");
  const { inputData, setInputData } = useContext(AppContext);

  const inputs: Input[] = [
    {
      name: "firstName",
      type: "text",
      label: t("form.first_name"),
      id: "firstName",
      value: inputData?.firstName,
    },
    {
      name: "lastName",
      type: "text",
      label: t("form.last_name"),
      id: "lastName",
      value: inputData?.lastName,
    },
    {
      name: "organisation",
      type: "text",
      label: t("form.organisation"),
      id: "organisation",
      value: inputData?.organisation,
      cols: "col-span-2",
    },
    {
      name: "email",
      type: "email",
      label: t("form.email"),
      id: "email",
      value: inputData?.email,
      cols: "col-span-2",
    },
  ];

  const resetDetails = () =>
    setInputData &&
    setInputData({
      ...inputData,
      firstName: undefined,
      lastName: undefined,
      organisation: undefined,
      email: undefined,
    });

  useEffect(() => {
    const subscription = watch(
      ({ firstName, lastName, organisation, email }) => {
        setInputData &&
          setInputData({
            ...inputData,
            firstName,
            lastName,
            organisation,
            email,
          });
      },
    );

    return () => subscription.unsubscribe();
  }, [inputData, setInputData, watch]);

  return (
    <div className="mt-10 flex flex-col lg:grid lg:grid-cols-2 lg:gap-x-5">
      {inputs.map(({ name, type, label, id, value, cols }) => (
        <div
          className={classNames(
            "relative mb-4 flex items-center justify-between gap-x-3 sm:gap-x-5 lg:gap-x-2",
            cols && "lg:col-span-2",
          )}
          key={id}
        >
          <label className="min-w-44 text-md font-semibold" htmlFor={id}>
            {label}
          </label>
          <Input
            register={register}
            variant={errors[name] ? "error" : "normal"}
            className="w-full"
            id={id}
            name={name}
            value={value || ""}
            type={type}
            aria-invalid={errors[name] ? "true" : "false"}
          />
          {errors[name] && (
            <p
              className="absolute -bottom-2 right-2 rounded-lg border border-red-600 bg-white px-1 py-[0.1rem] text-red-600 lg:text-xs"
              role="alert"
            >
              {errors[name]?.message?.toString()}
            </p>
          )}
        </div>
      ))}

      <div className="mb-1 mt-8 flex items-start text-left text-sm lg:mt-4 lg:justify-between">
        <p className="mb-5 mr-3">{t("consent")}</p>
        <input
          className="mr-1 mt-2 min-w-[1.3rem] scale-150 cursor-pointer accent-hot-pink"
          type="checkbox"
          required
          data-testid="consent-checkbox"
        />
      </div>
      <div className="mb-4 lg:mb-0 lg:mt-4">
        <Button
          onClick={resetDetails}
          className="w-full uppercase lg:ml-[15%] lg:w-[85%]"
          variant="secondary"
          type="button"
        >
          {t("form.clear_form")}
        </Button>
      </div>
    </div>
  );
}
